/**
 * Runs the bell count fetch and retries it if it fails.
 *
 * The count is fetched once per signed-in session, so a failed attempt must
 * not be mistaken for a completed one. There is no other way back into it: the
 * socket pushes a fresh count when a notification arrives, not when it
 * connects, so a blip at startup would otherwise leave the badge reading zero
 * over unread items for the rest of the visit.
 *
 * @param {Object} params
 * @param {function(): Promise<boolean>} params.getUnreadCount Resolves false only when the fetch failed.
 * @param {number} params.retries Attempts allowed after the first one.
 * @param {number} params.delayMs Gap between attempts.
 * @param {function(): void} [params.onGiveUp] Called once every attempt has failed, and never after cancelling.
 * @returns {function(): void} Cancels any pending retry. Safe to call more than once.
 */
export const fetchUnreadCountWithRetry = ({ getUnreadCount, retries, delayMs, onGiveUp }) => {
  let isCancelled = false;
  let retryTimer;

  const attempt = async retriesLeft => {
    const fetched = await getUnreadCount();

    if (fetched || isCancelled) {
      return;
    }

    if (retriesLeft <= 0) {
      onGiveUp?.();

      return;
    }

    retryTimer = setTimeout(() => {
      void attempt(retriesLeft - 1);
    }, delayMs);
  };

  void attempt(retries);

  return () => {
    isCancelled = true;
    clearTimeout(retryTimer);
  };
};

export default fetchUnreadCountWithRetry;
